<admin-app-header></admin-app-header>

<div class="market-insights-page">
    <div class="container-fluid">
        <!-- Page Header -->
        <div class="page-header">
            <div class="page-header__title">
                <h1><i class="fa-solid fa-city"></i> SA Parking Market Insights</h1>
                <p class="page-header__subtitle">Proprietary market intelligence from real ParkUpp transaction data across South Africa</p>
            </div>
            <div class="page-header__actions">
                <button class="btn btn-sm btn-outline-secondary" (click)="refreshData()" [disabled]="isLoading">
                    <i class="fa-solid fa-refresh" [class.fa-spin]="isLoading"></i> Refresh
                </button>
            </div>
        </div>

        <!-- Filters -->
        <div class="filter-bar">
            <form class="row align-items-end g-3" [formGroup]="filterForm">
                <div class="col-md-2">
                    <label class="form-label">Date Range (subscriptions)</label>
                    <select class="form-select form-select-sm" formControlName="filterDateRange">
                        <option *ngFor="let option of dateRangeOptions" [value]="option.value">{{ option.label }}</option>
                    </select>
                </div>
                <div class="col-md-2" *ngIf="isCustomDateRange">
                    <label class="form-label">Start</label>
                    <input type="date" class="form-control form-control-sm" formControlName="customStartDate" />
                </div>
                <div class="col-md-2" *ngIf="isCustomDateRange">
                    <label class="form-label">End</label>
                    <input type="date" class="form-control form-control-sm" formControlName="customEndDate" />
                </div>
                <div class="col-md-3">
                    <label class="form-label">Cities</label>
                    <ng-select
                        [items]="allCities"
                        [multiple]="true"
                        [closeOnSelect]="false"
                        placeholder="All cities"
                        formControlName="selectedCities"
                    ></ng-select>
                </div>
                <div class="col-md-1">
                    <label class="form-label">Min n</label>
                    <input type="number" class="form-control form-control-sm" formControlName="minSampleSize" min="1" max="20" />
                </div>
                <div class="col-auto" *ngIf="unresolvedCount > 0">
                    <span class="badge bg-warning text-dark">{{ unresolvedCount }} unresolved location{{ unresolvedCount !== 1 ? 's' : '' }}</span>
                </div>
            </form>
        </div>

        <!-- Loading State -->
        <div *ngIf="isLoading" class="loading-state">
            <div class="spinner"></div>
            <p>{{ loadingMessage }}</p>
        </div>

        <!-- ═══ Dashboard Content ═══ -->
        <div *ngIf="!isLoading && headline">

            <!-- ─── A. National Headline Strip ─── -->
            <div class="section-label">National Overview</div>
            <div class="summary-cards summary-cards--four">
                <div class="summary-card summary-card--info">
                    <div class="summary-card__icon"><i class="fa-solid fa-building"></i></div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ headline.publishedBays | number }}</span>
                        <span class="summary-card__label">Published Bays</span>
                        <span class="summary-card__subtext">{{ headline.publishedBuildings | number }} buildings</span>
                    </div>
                </div>
                <div class="summary-card summary-card--success">
                    <div class="summary-card__icon"><i class="fa-solid fa-car"></i></div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ headline.activeBays | number }}</span>
                        <span class="summary-card__label">Active Bays</span>
                        <span class="summary-card__subtext">Bays with active subscriptions</span>
                    </div>
                </div>
                <div class="summary-card summary-card--purple">
                    <div class="summary-card__icon"><i class="fa-solid fa-tag"></i></div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">R{{ headline.medianPriceRands | number: '1.0-0' }}</span>
                        <span class="summary-card__label">Median Monthly Price / Bay</span>
                        <span class="summary-card__subtext">Published MONTHLY listings only</span>
                    </div>
                </div>
                <div class="summary-card summary-card--warning">
                    <div class="summary-card__icon"><i class="fa-solid fa-file-contract"></i></div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">{{ headline.avgContractMonths | number: '1.1-1' }} mo</span>
                        <span class="summary-card__label">Avg Contract Length</span>
                        <span class="summary-card__subtext">{{ headline.avgBaysPerContract | number: '1.1-1' }} bays/contract avg</span>
                    </div>
                </div>
            </div>
            <div class="summary-cards summary-cards--single">
                <div class="summary-card summary-card--info">
                    <div class="summary-card__icon"><i class="fa-solid fa-coins"></i></div>
                    <div class="summary-card__content">
                        <span class="summary-card__value">R{{ headline.totalContractValueRands | number: '1.0-0' }}</span>
                        <span class="summary-card__label">Total Active Contract Value</span>
                        <span class="summary-card__subtext">Sum of (price x bays x months) for active subscriptions</span>
                    </div>
                </div>
            </div>

            <!-- ─── B. City Comparison ─── -->
            <div class="section-label">City Comparison</div>

            <!-- Charts: Price / Occupancy / Contract Size -->
            <div class="chart-grid" *ngIf="priceComparisonChart.categories.length > 0">
                <div class="chart-block">
                    <div class="chart-block__header">
                        <i class="fa-solid fa-tag"></i>
                        <h3>Median Price by City (R/month)</h3>
                    </div>
                    <div class="chart-block__body">
                        <apx-chart
                            [series]="[priceComparisonChart.series[0]]"
                            [chart]="{ type: 'bar', height: 300, toolbar: { show: false } }"
                            [plotOptions]="{ bar: { horizontal: true, barHeight: '60%', distributed: true, borderRadius: 4 } }"
                            [xaxis]="{ categories: priceComparisonChart.categories }"
                            [colors]="['#3e8bff']"
                            [dataLabels]="{ enabled: true, style: { fontSize: '12px', fontWeight: 600 } }"
                            [legend]="{ show: false }"
                        ></apx-chart>
                    </div>
                </div>

                <div class="chart-block">
                    <div class="chart-block__header">
                        <i class="fa-solid fa-file-contract"></i>
                        <h3>Contract Size by City</h3>
                    </div>
                    <div class="chart-block__body">
                        <apx-chart
                            [series]="contractSizeChart.series"
                            [chart]="{ type: 'bar', height: 300, toolbar: { show: false } }"
                            [plotOptions]="{ bar: { horizontal: true, borderRadius: 4 } }"
                            [xaxis]="{ categories: contractSizeChart.categories }"
                            [colors]="contractSizeChart.colors || ['#6366f1', '#14b8a6']"
                            [dataLabels]="{ enabled: true, style: { fontSize: '11px' } }"
                            [legend]="{ position: 'top' }"
                        ></apx-chart>
                    </div>
                </div>
            </div>

            <!-- City Comparison Table -->
            <div class="table-block" *ngIf="cityRows.length > 0">
                <div class="table-block__header">
                    <i class="fa-solid fa-table"></i>
                    <h3>City Comparison Table</h3>
                    <span class="badge bg-secondary ms-2">n &ge; {{ minSampleSize }} highlighted</span>
                </div>
                <div class="table-responsive">
                    <table class="table table-sm table-hover">
                        <thead>
                            <tr>
                                <th>City</th>
                                <th class="text-end sortable" (click)="sortCityTable('buildings')">Buildings <i *ngIf="citySortColumn === 'buildings'" [class]="citySortDirection === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'"></i></th>
                                <th class="text-end sortable" (click)="sortCityTable('totalBays')">Total Bays <i *ngIf="citySortColumn === 'totalBays'" [class]="citySortDirection === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'"></i></th>
                                <th class="text-end sortable" (click)="sortCityTable('medianPrice')">Median R <i *ngIf="citySortColumn === 'medianPrice'" [class]="citySortDirection === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'"></i></th>
                                <th class="text-end">P25 - P75</th>
                                <th class="text-end sortable" (click)="sortCityTable('avgContractMonths')">Avg Months <i *ngIf="citySortColumn === 'avgContractMonths'" [class]="citySortDirection === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'"></i></th>
                                <th class="text-end sortable" (click)="sortCityTable('avgBaysPerContract')">Avg Bays <i *ngIf="citySortColumn === 'avgBaysPerContract'" [class]="citySortDirection === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'"></i></th>
                                <th class="text-end sortable" (click)="sortCityTable('searchCount')">Searches <i *ngIf="citySortColumn === 'searchCount'" [class]="citySortDirection === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'"></i></th>
                                <th class="text-end sortable" (click)="sortCityTable('leadCount')">Leads <i *ngIf="citySortColumn === 'leadCount'" [class]="citySortDirection === 'asc' ? 'fa-solid fa-sort-up' : 'fa-solid fa-sort-down'"></i></th>
                                <th class="text-end">n</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let row of cityRows" [class.low-sample]="isBelowThreshold(row)">
                                <td><strong>{{ row.city }}</strong></td>
                                <td class="text-end">{{ row.buildings | number }}</td>
                                <td class="text-end">{{ row.totalBays | number }}</td>
                                <td class="text-end">R{{ row.medianPrice | number: '1.0-0' }}</td>
                                <td class="text-end text-muted">R{{ row.p25Price | number: '1.0-0' }} - R{{ row.p75Price | number: '1.0-0' }}</td>
                                <td class="text-end">{{ row.avgContractMonths | number: '1.1-1' }}</td>
                                <td class="text-end">{{ row.avgBaysPerContract | number: '1.1-1' }}</td>
                                <td class="text-end">{{ row.searchCount | number }}</td>
                                <td class="text-end">{{ row.leadCount | number }}</td>
                                <td class="text-end"><span class="badge bg-light text-dark">{{ row.sampleSizeParkings }}p / {{ row.sampleSizeSubscriptions }}s</span></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- ─── C. Pricing Deep Dive ─── -->
            <div class="section-label">Pricing Deep Dive</div>

            <div class="chart-grid">
                <!-- Price Band Distribution -->
                <div class="chart-block" *ngIf="priceBandChart.series.length > 0">
                    <div class="chart-block__header">
                        <i class="fa-solid fa-layer-group"></i>
                        <h3>Price Band Distribution by City</h3>
                    </div>
                    <div class="chart-block__body">
                        <apx-chart
                            [series]="priceBandChart.series"
                            [chart]="{ type: 'bar', height: 320, toolbar: { show: false }, stacked: true }"
                            [plotOptions]="{ bar: { horizontal: true, borderRadius: 4 } }"
                            [xaxis]="{ categories: priceBandChart.categories }"
                            [colors]="priceBandChart.colors || ['#4bd181', '#3e8bff', '#f59e0b', '#ef4444', '#a78bfa']"
                            [dataLabels]="{ enabled: false }"
                            [legend]="{ position: 'top' }"
                        ></apx-chart>
                    </div>
                </div>

                <!-- Amenity Premium -->
                <div class="chart-block" *ngIf="amenityPremiums.length > 0">
                    <div class="chart-block__header">
                        <i class="fa-solid fa-star"></i>
                        <h3>Amenity Price Premium</h3>
                    </div>
                    <div class="chart-block__body">
                        <apx-chart
                            [series]="amenityPremiumChart.series"
                            [chart]="{ type: 'bar', height: 250, toolbar: { show: false } }"
                            [plotOptions]="{ bar: { horizontal: true, barHeight: '60%', distributed: true, borderRadius: 4 } }"
                            [xaxis]="{ categories: amenityPremiumChart.categories }"
                            [colors]="amenityPremiumChart.colors || ['#4bd181']"
                            [dataLabels]="dataLabelsPremium"
                            [legend]="{ show: false }"
                        ></apx-chart>
                        <div class="amenity-detail">
                            <table class="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Amenity</th>
                                        <th class="text-end">With (avg R)</th>
                                        <th class="text-end">Without (avg R)</th>
                                        <th class="text-end">Premium</th>
                                        <th class="text-end">n (with/without)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr *ngFor="let a of amenityPremiums">
                                        <td>{{ a.label }}</td>
                                        <td class="text-end">R{{ a.withPrice | number: '1.0-0' }}</td>
                                        <td class="text-end">R{{ a.withoutPrice | number: '1.0-0' }}</td>
                                        <td class="text-end" [class.text-success]="a.premiumPct > 0" [class.text-danger]="a.premiumPct < 0">
                                            {{ a.premiumPct > 0 ? '+' : '' }}{{ a.premiumPct | number: '1.1-1' }}%
                                        </td>
                                        <td class="text-end text-muted">{{ a.withCount }} / {{ a.withoutCount }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Asking vs Achieved -->
            <div class="chart-block chart-block--full" *ngIf="askingVsAchieved.length > 0">
                <div class="chart-block__header">
                    <i class="fa-solid fa-scale-balanced"></i>
                    <h3>Asking vs Achieved Price (Negotiation Index)</h3>
                </div>
                <div class="chart-block__body">
                    <apx-chart
                        [series]="askingVsAchievedChart.series"
                        [chart]="{ type: 'bar', height: 300, toolbar: { show: false } }"
                        [plotOptions]="{ bar: { horizontal: false, columnWidth: '50%', borderRadius: 4 } }"
                        [xaxis]="{ categories: askingVsAchievedChart.categories }"
                        [colors]="askingVsAchievedChart.colors || ['#94a3b8', '#3e8bff']"
                        [dataLabels]="{ enabled: true, style: { fontSize: '11px' } }"
                        [legend]="{ position: 'top' }"
                        [yaxis]="{ title: { text: 'R / month / bay' } }"
                    ></apx-chart>
                    <div class="table-responsive mt-3">
                        <table class="table table-sm">
                            <thead>
                                <tr>
                                    <th>City</th>
                                    <th class="text-end">Listed (Median)</th>
                                    <th class="text-end">Transacted (Median)</th>
                                    <th class="text-end">Discount</th>
                                    <th class="text-end">n</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let row of askingVsAchieved">
                                    <td>{{ row.city }}</td>
                                    <td class="text-end">R{{ row.askingMedian | number: '1.0-0' }}</td>
                                    <td class="text-end">R{{ row.achievedMedian | number: '1.0-0' }}</td>
                                    <td class="text-end" [class.text-success]="row.discountPct > 0">
                                        {{ row.discountPct > 0 ? '-' : '' }}{{ row.discountPct | number: '1.1-1' }}%
                                    </td>
                                    <td class="text-end text-muted">{{ row.sampleSize }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- Suburb League Tables -->
            <div class="chart-grid" *ngIf="suburbMost.length > 0">
                <div class="table-block">
                    <div class="table-block__header">
                        <i class="fa-solid fa-arrow-up text-danger"></i>
                        <h3>Most Expensive Suburbs</h3>
                    </div>
                    <div class="table-responsive">
                        <table class="table table-sm">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Suburb</th>
                                    <th>City</th>
                                    <th class="text-end">Median R</th>
                                    <th class="text-end">Bays</th>
                                    <th class="text-end">n</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let s of suburbMost; let i = index">
                                    <td>{{ i + 1 }}</td>
                                    <td><strong>{{ s.suburb }}</strong></td>
                                    <td class="text-muted">{{ s.city }}</td>
                                    <td class="text-end">R{{ s.medianPrice | number: '1.0-0' }}</td>
                                    <td class="text-end">{{ s.bayCount | number }}</td>
                                    <td class="text-end text-muted">{{ s.parkingCount }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <div class="table-block">
                    <div class="table-block__header">
                        <i class="fa-solid fa-arrow-down text-success"></i>
                        <h3>Least Expensive Suburbs</h3>
                    </div>
                    <div class="table-responsive">
                        <table class="table table-sm">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Suburb</th>
                                    <th>City</th>
                                    <th class="text-end">Median R</th>
                                    <th class="text-end">Bays</th>
                                    <th class="text-end">n</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let s of suburbLeast; let i = index">
                                    <td>{{ i + 1 }}</td>
                                    <td><strong>{{ s.suburb }}</strong></td>
                                    <td class="text-muted">{{ s.city }}</td>
                                    <td class="text-end">R{{ s.medianPrice | number: '1.0-0' }}</td>
                                    <td class="text-end">{{ s.bayCount | number }}</td>
                                    <td class="text-end text-muted">{{ s.parkingCount }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <!-- ─── D. Demand & Scarcity ─── -->
            <div class="section-label">Demand &amp; Scarcity</div>

            <div class="chart-block chart-block--full" *ngIf="leadReasonsChart.categories.length > 0">
                <div class="chart-block__header">
                    <i class="fa-solid fa-magnifying-glass-location"></i>
                    <h3>Unmet Demand: Lead Reasons by City</h3>
                </div>
                <div class="chart-block__body">
                    <apx-chart
                        [series]="leadReasonsChart.series"
                        [chart]="{ type: 'bar', height: 320, toolbar: { show: false }, stacked: true }"
                        [plotOptions]="{ bar: { horizontal: true, borderRadius: 4 } }"
                        [xaxis]="{ categories: leadReasonsChart.categories }"
                        [colors]="leadReasonsChart.colors || ['#f97316', '#ef4444', '#a78bfa', '#3e8bff', '#94a3b8']"
                        [dataLabels]="{ enabled: false }"
                        [legend]="{ position: 'top' }"
                    ></apx-chart>
                </div>
            </div>

            <!-- Scarcity table -->
            <div class="table-block" *ngIf="cityRows.length > 0">
                <div class="table-block__header">
                    <i class="fa-solid fa-fire"></i>
                    <h3>Scarcity Index (Searches per Available Bay)</h3>
                </div>
                <div class="table-responsive">
                    <table class="table table-sm">
                        <thead>
                            <tr>
                                <th>City</th>
                                <th class="text-end">Searches</th>
                                <th class="text-end">Available Bays</th>
                                <th class="text-end">Scarcity Index</th>
                                <th class="text-end">Leads</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let row of cityRows" [class.low-sample]="isBelowThreshold(row)">
                                <td><strong>{{ row.city }}</strong></td>
                                <td class="text-end">{{ row.searchCount | number }}</td>
                                <td class="text-end">{{ (row.totalBays - row.activeBays) | number }}</td>
                                <td class="text-end">
                                    <span *ngIf="isFinite(row.demandIndex)">{{ row.demandIndex | number: '1.1-1' }}</span>
                                    <span *ngIf="!isFinite(row.demandIndex)" class="text-danger fw-bold">No supply</span>
                                </td>
                                <td class="text-end">{{ row.leadCount | number }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- ─── E. Uptake Over Time ─── -->
            <div class="section-label">Uptake Over Time</div>

            <div class="chart-grid">
                <div class="chart-block" *ngIf="uptakeChart.categories.length > 1">
                    <div class="chart-block__header">
                        <i class="fa-solid fa-chart-line"></i>
                        <h3>Monthly New Contracts &amp; Bays</h3>
                    </div>
                    <div class="chart-block__body">
                        <apx-chart
                            [series]="uptakeChart.series"
                            [chart]="{ type: 'line', height: 320, toolbar: { show: false } }"
                            [xaxis]="{ categories: uptakeChart.categories }"
                            [colors]="uptakeChart.colors || ['#3e8bff', '#4bd181']"
                            [stroke]="{ width: [3, 3], curve: 'smooth' }"
                            [legend]="{ position: 'top' }"
                            [dataLabels]="{ enabled: false }"
                        ></apx-chart>
                    </div>
                </div>

                <div class="chart-block" *ngIf="seasonalityChart.categories.length > 0">
                    <div class="chart-block__header">
                        <i class="fa-solid fa-calendar"></i>
                        <h3>Seasonality (All Years)</h3>
                    </div>
                    <div class="chart-block__body">
                        <apx-chart
                            [series]="seasonalityChart.series"
                            [chart]="{ type: 'bar', height: 320, toolbar: { show: false } }"
                            [plotOptions]="{ bar: { columnWidth: '60%', borderRadius: 4 } }"
                            [xaxis]="{ categories: seasonalityChart.categories }"
                            [colors]="seasonalityChart.colors || ['#6366f1']"
                            [dataLabels]="{ enabled: true, style: { fontSize: '11px' } }"
                            [legend]="{ show: false }"
                        ></apx-chart>
                    </div>
                </div>
            </div>

            <!-- ─── F. Commitment & Retention ─── -->
            <div class="section-label">Commitment &amp; Retention</div>

            <div class="chart-grid">
                <!-- Term Mix -->
                <div class="chart-block" *ngIf="termMixChart.categories.length > 0">
                    <div class="chart-block__header">
                        <i class="fa-solid fa-shapes"></i>
                        <h3>Term Mix by City</h3>
                    </div>
                    <div class="chart-block__body">
                        <apx-chart
                            [series]="termMixChart.series"
                            [chart]="{ type: 'bar', height: 300, toolbar: { show: false }, stacked: true, stackType: '100%' }"
                            [plotOptions]="{ bar: { horizontal: true, borderRadius: 4 } }"
                            [xaxis]="{ categories: termMixChart.categories }"
                            [colors]="termMixChart.colors || ['#3e8bff', '#4bd181', '#f59e0b']"
                            [dataLabels]="{ enabled: false }"
                            [legend]="{ position: 'top' }"
                        ></apx-chart>
                    </div>
                </div>

                <!-- Contract Duration Distribution -->
                <div class="chart-block" *ngIf="durationChart.categories.length > 0">
                    <div class="chart-block__header">
                        <i class="fa-solid fa-clock"></i>
                        <h3>Contract Duration Distribution</h3>
                    </div>
                    <div class="chart-block__body">
                        <apx-chart
                            [series]="durationChart.series"
                            [chart]="{ type: 'bar', height: 280, toolbar: { show: false } }"
                            [plotOptions]="{ bar: { columnWidth: '60%', borderRadius: 4, distributed: true } }"
                            [xaxis]="{ categories: durationChart.categories }"
                            [colors]="['#3e8bff', '#4bd181', '#f59e0b', '#a78bfa', '#ef4444']"
                            [dataLabels]="{ enabled: true, style: { fontSize: '12px', fontWeight: 600 } }"
                            [legend]="{ show: false }"
                        ></apx-chart>
                    </div>
                </div>
            </div>

            <!-- Renewal Rates Table -->
            <div class="chart-grid" *ngIf="renewalRates.length > 0">
                <div class="table-block">
                    <div class="table-block__header">
                        <i class="fa-solid fa-rotate"></i>
                        <h3>Renewal Rates by City</h3>
                    </div>
                    <div class="table-responsive">
                        <table class="table table-sm">
                            <thead>
                                <tr>
                                    <th>City</th>
                                    <th class="text-end">Renewed</th>
                                    <th class="text-end">Not Renewed</th>
                                    <th class="text-end">Pending</th>
                                    <th class="text-end">Renewal Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr *ngFor="let row of renewalRates">
                                    <td><strong>{{ row.city }}</strong></td>
                                    <td class="text-end">{{ row.renewed | number }}</td>
                                    <td class="text-end">{{ row.notRenewed | number }}</td>
                                    <td class="text-end text-muted">{{ row.pending | number }}</td>
                                    <td class="text-end" [class.text-success]="row.renewalRate >= 50" [class.text-danger]="row.renewalRate < 50">
                                        {{ row.renewalRate | number: '1.1-1' }}%
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Non-Renewal Reasons -->
                <div class="chart-block" *ngIf="nonRenewalChart.categories.length > 0">
                    <div class="chart-block__header">
                        <i class="fa-solid fa-door-open"></i>
                        <h3>Non-Renewal Reasons</h3>
                    </div>
                    <div class="chart-block__body">
                        <apx-chart
                            [series]="nonRenewalChart.series"
                            [chart]="{ type: 'bar', height: 250, toolbar: { show: false } }"
                            [plotOptions]="{ bar: { horizontal: true, barHeight: '60%', distributed: true, borderRadius: 4 } }"
                            [xaxis]="{ categories: nonRenewalChart.categories }"
                            [colors]="nonRenewalChart.colors || ['#3e8bff']"
                            [dataLabels]="{ enabled: true, style: { fontSize: '12px', fontWeight: 600 } }"
                            [legend]="{ show: false }"
                        ></apx-chart>
                    </div>
                </div>
            </div>

            <!-- Methodology footnote -->
            <div class="methodology-note">
                <i class="fa-solid fa-circle-info"></i>
                <strong>Methodology:</strong>
                Only published parkings with MONTHLY-term plans are included in price statistics.
                Median is used for prices (property pricing is skewed). Cities with fewer than {{ minSampleSize }} parkings are greyed out.
                Search log limited to most recent 30,000 entries.
            </div>
        </div>
    </div>
</div>
